import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { Block16, CheckCircle16, ChevronDown, ChevronUp, Close12, CloseCircle16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Modal } from '@makyoapp/makyo-ui'

import { FileObj, UploadStatus, useUploadContext } from '@/context/UploadContext'

const uploadStatusMap = {
  [UploadStatus.Waiting]: 'Waiting',
  [UploadStatus.Uploading]: 'Uploading',
  [UploadStatus.Uploaded]: 'Uploaded',
  [UploadStatus.Error]: 'Error',
  [UploadStatus.Aborted]: 'Upload Canceled',
}

interface UploadBarProps {
  show: boolean
}

export default function UploadBar({ show }: UploadBarProps) {
  const { t } = useTranslation()

  const { files, handleReset, status, timeEstimation } = useUploadContext()
  const entries = Object.entries(files)
  const autoShow = entries.length > 0

  const [forcedClose, setForcedClose] = useState(false)
  const [showWarningCloseModal, setShowWarningCloseModal] = useState(false)
  const [open, setOpen] = useBoolean(true)

  const uploadIncomplete = entries.some(
    ([, file]) => file.status === UploadStatus.Waiting || file.status === UploadStatus.Uploading
  )

  const handleForceClose = () => {
    handleReset()
    setForcedClose(true)
    setShowWarningCloseModal(false)
  }

  const handleCloseUploadBar = (e: any) => {
    e.stopPropagation()

    if (uploadIncomplete) {
      setShowWarningCloseModal(true)
    } else {
      handleForceClose()
    }
  }

  const handleCloseWarningModal = () => {
    setShowWarningCloseModal(false)
  }

  useEffect(() => {
    if (uploadIncomplete) {
      setForcedClose(false)
    }
  }, [uploadIncomplete])

  if (!autoShow || forcedClose) return null

  return (
    <>
      <div
        id='mk-upload-bar'
        className='mk-upload-bar fixed bottom-0 w-[424px] rounded-sm bg-white dark:bg-slate-900 dark:text-slate-300 shadow-md ltr:right-4 rtl:left-4'
      >
        <div
          onClick={() => setOpen(!open)}
          className='mk-upload-bar-header flex cursor-pointer w-full text-start rounded-t-md px-4 py-2 font-bold bg-teal-500 text-white items-center'
        >
          <div className='w-full'>
            <Trans>Upload</Trans>
          </div>
          <div className='shrink-0 flex items-center gap-2'>
            <span
              role='button'
              onClick={() => setOpen(!open)}
              className='mk-upload-bar-header-button p-1.5 aspect-square rounded-full flex items-center justify-center hover:bg-teal-600 transition'
              title={t('Hide Upload Bar')}
            >
              {open ? <ChevronDown /> : <ChevronUp />}
            </span>

            <span
              role='button'
              onClick={handleCloseUploadBar}
              className='mk-upload-bar-header-button p-1.5 aspect-square rounded-full flex items-center justify-center hover:bg-teal-600 transition'
              title={t('Close Upload Bar')}
            >
              <Close12 xlinkTitle='Close' />
            </span>
          </div>
        </div>

        {open && (
          <div className='mk-upload-bar-body flex flex-col overflow-y-scroll max-h-[380px]'>
            {entries.length < 1 ? (
              <div className='p-4'>No Upload Queue</div>
            ) : (
              <>
                {status === 'started' && (
                  <div className='px-4 py-2 text-xs bg-slate-200 dark:bg-slate-800 flex items-center justify-between'>
                    <div className='text-light'>{timeEstimation}</div>

                    <span role='button' className='font-medium' onClick={handleCloseUploadBar}>
                      Cancel
                    </span>
                  </div>
                )}

                {entries.map(([id, file]) => {
                  return <UploadBarItem key={id} file={file} />
                })}
              </>
            )}
          </div>
        )}
      </div>

      {showWarningCloseModal && (
        <Modal show={true}>
          <Modal.Header onCloseClick={handleCloseWarningModal}>{t('Cancel Upload?')}</Modal.Header>
          <Modal.Body className='modal-sm'>
            <div className='py-2'>{t('Your upload is not complete. Would you like to cancel the upload?')}</div>
          </Modal.Body>
          <Modal.Footer className='flex justify-between'>
            <Button variant={ButtonVariant.Outlined} onClick={handleForceClose}>
              {t('Cancel Upload')}
            </Button>
            <Button onClick={handleCloseWarningModal}>{t('Continue Upload')}</Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

const UploadBarItem = ({ file }: { file: FileObj }) => {
  const { t } = useTranslation()

  const [showCancel, setShowCancel] = useState(false)

  const finishedUpload = [UploadStatus.Uploaded, UploadStatus.Aborted, UploadStatus.Error].includes(file.status)

  const statusText = uploadStatusMap[file.status]

  const handleToggleCancelBtn = () => {
    if (file.status === UploadStatus.Waiting) {
      setShowCancel((prev) => !prev)
    }
  }

  const CancelButton = () => (
    <button
      title='Cancel Upload'
      id={`${file.id}-abort`}
      data-id='abort'
      onClick={() => {
        file.controller?.abort()
      }}
    >
      <CloseCircle16 className='red' />
    </button>
  )

  return (
    <div
      className='mk-upload-bar-file flex items-center gap-6 px-4 py-3 border-b border-b-zinc-200 dark:border-b-slate-800'
      onMouseEnter={handleToggleCancelBtn}
      onMouseLeave={handleToggleCancelBtn}
    >
      <div className='txt w-full h-full flex items-center gap-3'>
        <div className={clsx('txt', !finishedUpload && 'opacity-40')} title={file.file.name}>
          {file.file.name}
        </div>

        {file.status === UploadStatus.Aborted && <div className='text-[10px] text-light'>{t(statusText)}</div>}
      </div>

      <div className='shrink-0 h-full' title={statusText}>
        {file.status === UploadStatus.Waiting && (
          <div className='border-2 w-5 h-5 rounded-full'>{showCancel && <CancelButton />}</div>
        )}

        {file.status === UploadStatus.Uploading && (
          <div className='flex-c'>
            <CancelButton />
            <span className='loading spin w-4! h-4!' />
          </div>
        )}

        {file.status === UploadStatus.Uploaded && <CheckCircle16 className='text-primary-500' />}

        {file.status === UploadStatus.Error && <CloseCircle16 className='red' />}

        {file.status === UploadStatus.Aborted && <Block16 className='text-amber-500' />}
      </div>
    </div>
  )
}
